import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { pricingPlans } from '../mock';

export const Pricing = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="py-16 md:py-32 bg-[#0A0E14] relative overflow-hidden">
      <div className="absolute inset-0 pro-grid opacity-30"></div>
      <div className="ambient-glow absolute top-20 left-10 w-[400px] md:w-[500px] h-[400px] md:h-[500px] bg-green-500"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 md:mb-20">
          <div className="inline-block mb-3 md:mb-4">
            <span className="text-green-400 text-[10px] md:text-xs font-semibold uppercase tracking-wider">Pricing Plans</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 px-4">
            Simple, <span className="text-green-400">Predictable</span> Pricing
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto px-4">
            No hidden fees. Scale as you grow.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <div
              key={plan.id}
              className={`group ${plan.highlighted ? 'md:-mt-6' : ''}`}
              style={{ animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards', animationDelay: `${index * 150}ms`, opacity: 0 }}
            >
              {plan.highlighted && (
                <div className="flex justify-center md:absolute md:-top-4 md:left-1/2 md:transform md:-translate-x-1/2 z-20 mb-4 md:mb-0">
                  <div className="px-3 md:px-4 py-1 md:py-1.5 bg-green-500 rounded-full text-black text-[10px] md:text-xs font-bold">
                    Most Popular
                  </div>
                </div>
              )}
              
              <div className={`glass-card rounded-xl md:rounded-2xl p-6 md:p-10 h-full ${
                plan.highlighted ? 'border-green-500/30 shadow-xl shadow-green-500/10' : ''
              }`}>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                  {plan.name}
                </h3>
                <p className="text-gray-500 mb-6 md:mb-8 text-xs md:text-sm">
                  {plan.description}
                </p>
                
                <div className="mb-6 md:mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
                      {plan.price === 'Custom' ? 'Custom' : `₹${plan.price}`}
                    </span>
                    {plan.period && <span className="text-gray-500 text-sm md:text-base">/{plan.period}</span>}
                  </div>
                </div>
                
                <Button
                  onClick={() => scrollToSection('cta')}
                  className={`w-full py-4 md:py-5 mb-6 md:mb-8 font-semibold transition-all duration-200 text-sm md:text-base ${
                    plan.highlighted
                      ? 'bg-green-500 hover:bg-green-400 text-black shadow-lg shadow-green-500/20'
                      : 'bg-white/5 hover:bg-white/10 text-white border border-gray-700 hover:border-gray-600'
                  }`}
                >
                  {plan.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                  <ArrowRight className="ml-2 inline" size={16} />
                </Button>
                
                <ul className="space-y-2 md:space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 md:gap-3">
                      <div className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 mt-0.5 bg-green-500/10 rounded flex items-center justify-center">
                        <Check className="text-green-400" size={12} />
                      </div>
                      <span className="text-gray-400 text-xs md:text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 md:mt-12 text-center px-4">
          <p className="text-gray-500 text-sm md:text-base">
            Need a custom solution?{' '}
            <button onClick={() => scrollToSection('cta')} className="text-green-400 font-semibold hover:text-green-300 transition-colors">
              Talk to our team
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};